/* Schema.org structured-data builders for the Lumetier marketing surfaces
   and the founder Allure Image studio routes. Returns plain JSON-LD objects
   that useStructuredData() then injects as <script> tags.

   Why structured data matters: Google's rich-results layer uses LocalBusiness
   for the knowledge panel (hours, address, phone, ratings), Organization for
   the brand graph (logo, sameAs social links, founder), OfferCatalog so
   service prices surface in SERPs, and CollectionPage so the portfolio gets
   indexed as a curated collection rather than a flat image grid.

   These are static builders — no API calls, no React state. Keeps the schema
   deterministic so Google sees the same JSON-LD on every render. */

const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

// ─── Allure (founding studio) ────────────────────────────────────────────
const ALLURE_BASE = "https://allure-image.com";
const ALLURE_LOGO = `${ALLURE_BASE}/lumetier-wordmark.png`;
const ALLURE_HERO = `${ALLURE_BASE}/hero/allure-home-hero.jpg`;
const ALLURE_PHONE = "+1-973-555-0173";

/** LocalBusiness root — the entity Google uses for the knowledge panel.
 *  Includes hours, address, contact, geo radius, founder, image references. */
export function allureLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "PhotographStore",
    "@id": `${ALLURE_BASE}/#studio`,
    name: "Allure Image",
    alternateName: "Allure Image Studio",
    description:
      "Editorial photography studio in Newark, New Jersey. Refined portraits, brand stories, maternity, weddings, family editions, and milestone sessions, delivered as cinema-graded editorial sets.",
    url: ALLURE_BASE,
    logo: ALLURE_LOGO,
    image: ALLURE_HERO,
    telephone: ALLURE_PHONE,
    email: "hello@allure-image.com",
    priceRange: "$$$",
    paymentAccepted: "Zelle, Stripe, Credit Card",
    currenciesAccepted: "USD",
    address: {
      "@type": "PostalAddress",
      streetAddress: "212 Hawthorne Lane",
      addressLocality: "Newark",
      addressRegion: "NJ",
      postalCode: "07102",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 40.7357, longitude: -74.1724 },
    areaServed: [
      { "@type": "AdministrativeArea", name: "New Jersey" },
      { "@type": "AdministrativeArea", name: "New York" },
      { "@type": "AdministrativeArea", name: "Pennsylvania" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "10:00",
        closes: "18:00",
      },
    ],
    foundingDate: "2021",
    founder: { "@type": "Person", name: "Bisi Akinrinade" },
    sameAs: [
      "https://www.instagram.com/allureimage/",
      "https://lumetiercreatives.com/s/allure-image",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      bestRating: "5",
      ratingCount: "27",
    },
  };
}

/** ItemList of editorial services — drives "service" snippets in search. */
export function allureServicesCatalogSchema() {
  const services = [
    {
      name: "Editorial Portrait Sitting",
      description:
        "A 90-minute studio sitting for a refined personal portrait. Includes wardrobe consultation and a curated print credit.",
      price: "1400",
    },
    {
      name: "Brand Story Half-Day",
      description:
        "Four hours on location capturing your craft — product, process, portrait. Delivered as a small editorial set.",
      price: "2200",
    },
    {
      name: "Milestone Session",
      description:
        "Weddings, anniversaries, family editions. A full day of unhurried storytelling, paired with an heirloom album.",
      price: "3200",
    },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": `${ALLURE_BASE}/services#catalog`,
    name: "Allure Image · Editorial Photography Services",
    url: `${ALLURE_BASE}/services`,
    provider: { "@id": `${ALLURE_BASE}/#studio` },
    itemListElement: services.map((svc, i) => ({
      "@type": "Offer",
      position: i + 1,
      itemOffered: {
        "@type": "Service",
        name: svc.name,
        description: svc.description,
        provider: { "@id": `${ALLURE_BASE}/#studio` },
      },
      price: svc.price,
      priceCurrency: "USD",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: svc.price,
        priceCurrency: "USD",
        valueAddedTaxIncluded: false,
      },
      availability: "https://schema.org/InStock",
      url: `${ALLURE_BASE}/booking?service=${encodeURIComponent(svc.name.toLowerCase().replace(/\s+/g, "-"))}`,
    })),
  };
}

/** CollectionPage — frames the portfolio as a curated set, not a flat grid. */
export function allurePortfolioCollectionSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${ALLURE_BASE}/portfolio#collection`,
    name: "Allure Image · Selected Portfolio · 2021—2026",
    description:
      "A curated cross-section of Allure Image's editorial portrait work — studio sittings, location stories, and milestone sessions.",
    url: `${ALLURE_BASE}/portfolio`,
    isPartOf: { "@id": `${ALLURE_BASE}/#studio` },
    about: { "@id": `${ALLURE_BASE}/#studio` },
  };
}

/** ContactPage — pairs nicely with LocalBusiness for the contact route. */
export function allureContactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${ALLURE_BASE}/contact#page`,
    name: "Allure Image · Contact the Studio",
    url: `${ALLURE_BASE}/contact`,
    about: { "@id": `${ALLURE_BASE}/#studio` },
  };
}

// ─── Lumetier (SaaS hub) ─────────────────────────────────────────────────
const LUMETIER_BASE = "https://lumetiercreatives.com";

/** Organization for the SaaS brand — distinct from Allure's LocalBusiness.
 *  Drives the Lumetier knowledge graph entry (logo, sameAs, foundingDate). */
export function lumetierOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${LUMETIER_BASE}/#org`,
    name: "Lumetier",
    legalName: "Lumetier Creatives",
    url: LUMETIER_BASE,
    logo: `${LUMETIER_BASE}/lumetier-wordmark.png`,
    description:
      "The operating system for working photographers. Bookings, galleries, contracts, payments, and a public site — every tool a photographer needs, built by a working photographer.",
    foundingDate: "2026",
    sameAs: [
      "https://allure-image.com",
    ],
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "Customer Support",
      email: "hello@lumetiercreatives.com",
      availableLanguage: ["English"],
    }],
  };
}

/** SoftwareApplication — surfaces Lumetier in product-search contexts. */
export function lumetierSoftwareSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${LUMETIER_BASE}/#app`,
    name: "Lumetier",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Photography Studio Management",
    operatingSystem: "Web",
    url: LUMETIER_BASE,
    creator: { "@id": `${LUMETIER_BASE}/#org` },
    description:
      "Hosted studio management platform for photographers — public site, client booking flow, private galleries, contracts, payments, dashboard, and AI Copilot.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    featureList: [
      "Editorial public sites (Editorial, Bold, Minimal, Journal templates)",
      "Booking & calendar with deposit collection",
      "Private password-protected client galleries",
      "Contracts & invoices",
      "Custom domains via Cloudflare for SaaS",
      "AI Copilot for client correspondence",
    ],
  };
}

// Suppress the unused-warning since APP_URL is used for future tenant schemas
export { APP_URL };
